import { notification } from 'antd'
import i18next from 'i18next'
import { action, computed, makeAutoObservable, observable } from 'mobx'

import { CityDTO, StreetDTO } from 'services/yandexApi/types'
import { PickPointType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { BaseOption, Country } from 'shared/types/types'

import { ordersApi } from '../../api'
import { OrderKeys, subsidySumKey } from '../../data'
import { Coordinate, CouriersAccess, PickPointLocation, SubsidyHexType, SubsidyInfo } from '../../types'
import { getOrderModuleKey } from '../../utils'

export class OrderLocationViewModel {
  @observable pickPointType: PickPointType | null = null

  @observable cityItem: CityDTO | null = null
  @observable cityOptions: CityDTO[] = []
  @observable street: string = ''
  @observable streetOptions: StreetDTO[] = []
  @observable streetItem: StreetDTO | null = null
  @observable house: string = ''
  @observable houseWasSelected: boolean = false
  @observable coordinates: Coordinate = {
    longitude: '',
    latitude: '',
  }

  @observable subsidyHexTypeItem: BaseOption<SubsidyHexType> | null = null
  @observable subsidyInfo: SubsidyInfo[] = []
  @observable isSubsidyLoading = false

  @observable description: string = ''
  @observable courierWay: string = ''
  @observable couriersAccess: CouriersAccess = CouriersAccess.fulltime
  @observable floorLevel = PickPointLocation.firstFloor
  @observable entrance = PickPointLocation.firstLine

  constructor() {
    makeAutoObservable(this)
  }

  @computed
  get streetHouse() {
    let street = this.street

    if (this.streetItem !== null && this.house !== null && this.house.trim().length > 0) {
      street = `${street}, ${this.house}`
    }

    return street
  }

  getSubsidyTypeOptions = (country: Country) => {
    return this.subsidyInfo.map((subsidy) => {
      const subsidyKeyTranslated = i18next.t(`${OrderKeys.orderModule}.${subsidy.subsidyType}`)
      const subsidySumKeyTranslated = i18next.t(`${OrderKeys.orderModule}.${subsidySumKey[country]}`)

      const subsidyLabel =
        subsidy.subsidyType === SubsidyHexType.single
          ? `${subsidyKeyTranslated} ${subsidySumKeyTranslated}`
          : subsidyKeyTranslated

      return {
        value: subsidy.subsidyType,
        label: subsidyLabel,
      }
    })
  }

  getSubsidyItem = (country: Country) => {
    return this.getSubsidyTypeOptions(country).find((item) => item.value === this.subsidyHexTypeItem?.value)
  }

  @action
  setPickPointType = (value: PickPointType | null) => {
    this.pickPointType = value
  }

  @action
  setCityOptions = (value: CityDTO[]) => {
    this.cityOptions = value
  }

  @action
  setStreetOptions = (value: StreetDTO[]) => {
    this.streetOptions = value
  }

  @action
  setCityItem = (item: CityDTO | null) => {
    this.cityItem = item
  }

  @action
  setStreetItem = (value: StreetDTO | null, changeCoordinates: boolean = true) => {
    this.streetItem = value

    if (value == null) {
      this.setHouseWasSelected(false)
      this.setCoordinates({ latitude: '', longitude: '' })
    } else {
      this.setAddress(value)

      if (changeCoordinates) {
        this.setCoordinates({ latitude: value.lat || '', longitude: value.lng || '' })
      }
    }
  }

  @action
  setStreet = (value: string) => {
    this.street = value
  }

  @action
  setHouse = (value: string) => {
    this.house = value
  }

  @action
  setAddress = (value: StreetDTO) => {
    if (value.house) {
      this.setHouseWasSelected(true)

      this.setStreet(value.street)

      if (typeof value.street === 'string' && value.street.trim().length === 0) {
        const streetWithoutHouse = value.name
          .split(',')
          .map((x) => x.trim())
          .filter((x) => x !== value.house)
          .join(', ')

        this.setStreet(streetWithoutHouse)
      } else if (!value.name.includes(value.street)) {
        this.setStreet(value.name.split(',')[0] || value.street)
      }

      this.setHouse(value.house)
    } else if (value.kind === 'district') {
      this.setStreet(value.name)
    } else {
      this.setStreet(value.street)
    }
  }

  @action
  setCoordinates = (value: Coordinate) => {
    this.coordinates = value
  }

  @action
  setSubsidyHexTypeItem = (value: BaseOption<SubsidyHexType> | null) => {
    this.subsidyHexTypeItem = value
  }

  @action
  setSubsidyInfo = (value: SubsidyInfo[]) => {
    this.subsidyInfo = value
  }

  @action
  setIsSubsidyLoading = (value: boolean) => {
    this.isSubsidyLoading = value
  }

  @action
  setDescription = (value: string) => {
    this.description = value
  }

  @action
  setCourierWay = (value: string) => {
    this.courierWay = value
  }

  @action
  setFloorLevel = (value: PickPointLocation) => {
    this.floorLevel = value
  }

  @action
  setEntrance = (value: PickPointLocation) => {
    this.entrance = value
  }

  @action
  setCouriersAccess = (value: CouriersAccess) => {
    this.couriersAccess = value
  }

  @action
  private setHouseWasSelected = (value: boolean) => {
    this.houseWasSelected = value
  }

  @action
  resetSubsidy = () => {
    this.setSubsidyHexTypeItem(null)
    this.setSubsidyInfo([])
  }

  @action
  resetAddress = () => {
    this.setStreet('')
    this.setStreetOptions([])
    this.setStreetItem(null)
    this.setHouseWasSelected(false)
    this.setHouse('')
    this.setCoordinates({
      longitude: '',
      latitude: '',
    })
  }

  @action
  reset = () => {
    this.setPickPointType(null)
    this.setCityItem(null)
    this.setCityOptions([])
    this.resetAddress()
    this.resetSubsidy()
    this.setDescription('')
    this.setCourierWay('')
    this.setCouriersAccess(CouriersAccess.fulltime)
    this.setFloorLevel(PickPointLocation.firstFloor)
    this.setEntrance(PickPointLocation.firstLine)
  }

  async getSubsidy(tariff: number) {
    try {
      this.setIsSubsidyLoading(true)
      const res = await ordersApi.getSubsidy(this.coordinates, tariff)
      this.setSubsidyInfo(res)
    } catch (error) {
      notification.error({
        message: i18next.t(getOrderModuleKey('api.getSubsidyError')),
        description: (error as Error).message,
      })
    } finally {
      this.setIsSubsidyLoading(false)
    }
  }
}
